/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, catchError, forkJoin, map, of } from 'rxjs';
import {
	AppConstants,
	expAppConstants
} from '../../constants/portal-constants';
import { ApiResponse } from '../../definitions/api-response.interface';
import { ApiService } from '../../services/api/api.service';
import {
	UserRegistrationStatus
} from '../../services/api/definitions';
import { LocalStorageService } from '../../services/local-storage/local-storage.service';
import { LoggerService } from '../../services/logger/logger.service';
import { ApplicationPath, ErrorMessage } from '../../services/shared/shared-enums';
import { SharedUtilService } from '../../services/shared/shared-util.service';
import { SharedService } from '../../services/shared/shared.service';
import { AuthFactory } from './auth.factory';
import {
	AuthState, ProfileLocation, RoutingFunction, TMHCorporateAdminAuthState, ZendeskToken } from './definitions';
import { SharedApiService } from '../../services/shared/shared-api-call.service';
import { EnvironmentRef } from 'projects/tmhna-portal/src/environments/interfaces';
import { expEnvironmentRef } from '../../injection-tokens/environment.injection-token';
@Injectable({
	providedIn: 'root'
})
export class AuthService
{
	tnc: boolean = false;
	private state$: BehaviorSubject<AuthState> = new BehaviorSubject<AuthState>(
		AuthFactory.buildState()
	);
	get state(): AuthState
	{
		return this.state$.value;
	}
	get stateChanged(): Observable<AuthState>
	{
		return this.state$.asObservable();
	}
	userName = '';
	currentRole: string|undefined = '';
	user:AuthState= AuthFactory.buildState();
	constructor(
		private apiService: ApiService,
		private router: Router,
		private sharedApiService: SharedApiService,
		private loggerService: LoggerService,
		private localStorageService: LocalStorageService,
		private sharedService: SharedService,
		
		@Inject(expAppConstants) readonly appConstantsRef: AppConstants,
		private sharedUtilService: SharedUtilService,
		@Inject(expEnvironmentRef) readonly environmentRef: EnvironmentRef
	) 
	{ }

	public updateState(value: AuthState): void
	{
		this.state$.next(value);
	}

	public async redirectBasedOnUserRegistrationStatus(): Promise<void>
	{
		try
		{
			const status: ApiResponse = await this.getUserRegistrationStatus();

			const responseObject = <UserRegistrationStatus>status.data;
			this.localStorageService.setAssignedRoles(JSON.stringify(responseObject.assignedRoles));
			const currentRole: string = (responseObject.roles && responseObject.roles[0]) ? responseObject.roles[0] : '';
			this.localStorageService.setCurrentRole(currentRole);
			responseObject.registrationStatus = this.isPendingBusinessAccountApproval(responseObject) ? 'pending_business_approval' : responseObject.registrationStatus;
			const routingFunction: RoutingFunction | undefined = this.sharedUtilService.getRoutingFunction(responseObject);
			this.handleUserBasedOnStatus(responseObject, routingFunction);
			!this.environmentRef?.isRaymond && this.loadZendeskChat();
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		catch (error: any)
		{
			this.redirectOnError(error);
		}
	}

	private handleUserBasedOnStatus(responseObject: UserRegistrationStatus, routingFunction: RoutingFunction | undefined): void 
	{
		const isTmhCustomerSupportRole = this.localStorageService.getCustomerToDashboard();
		this.sharedUtilService.isCustomerSupportSession$.next(isTmhCustomerSupportRole);
		this.handleCustomerNavigation(responseObject, isTmhCustomerSupportRole);
		if (this.sharedUtilService.isTmhCorporateAdminUser(responseObject, isTmhCustomerSupportRole)) 
		{
			this.handleTmhCorporateAdminUser(routingFunction);
		}
		else if (this.sharedUtilService.isCustomerSupportUser(responseObject, isTmhCustomerSupportRole)  ) 
		{
			this.handleCustomerSupportUser(routingFunction);
		}
		else if (this.isNonTmhCorporateAdmin(responseObject) || isTmhCustomerSupportRole ) 
		{
			this.handleNonTmhCorporateAdminUser(routingFunction);
		}
		else if (routingFunction !== undefined) 
		{
			this.handleRoutingFunction(responseObject, routingFunction);
		}
	}

	private handleTmhCorporateAdminUser(routingFunction: RoutingFunction | undefined): void 
	{
		this.localStorageService.setItems('isTmhCorporateAdmin', 'true');
		this.sharedUtilService.isTmhCorporateAdmin = true;
		this.getLoggedInTmhCorporateAdminDetails(routingFunction);
	}
	
	private handleCustomerSupportUser(routingFunction: RoutingFunction | undefined): void 
	{
		this.localStorageService.setItems('isTmhCustomerSupportRole', 'true');
		this.sharedUtilService.isTmhCustomerSupportRole = true;
		this.getLoggedInTmhCustomerSupportDetails(routingFunction);
	}
	
	private handleNonTmhCorporateAdminUser(routingFunction: RoutingFunction | undefined): void 
	{
		this.localStorageService.setItems('isPortalUser', 'true');
		this.getLoggedInUserDetails(routingFunction);
		this.sharedUtilService.isLoggedInUserPresent();
	}
	
	private handleRoutingFunction(responseObject: UserRegistrationStatus, routingFunction: RoutingFunction): void 
	{
		this.sharedUtilService.handlePendingAndNotApprovedState(responseObject);
		this.sharedUtilService.handleRoutingFunction(routingFunction, this.router);
	}

	isPendingBusinessAccountApproval(responseObject: UserRegistrationStatus)
	{
		return (responseObject.hasOwnProperty('registrationStatus') && responseObject.registrationStatus === 'pending_approval' && responseObject.orgAdminPending);
	}

	isNonTmhCorporateAdmin(responseObject: UserRegistrationStatus)
	{
		return (responseObject.hasOwnProperty('registrationStatus') && responseObject.registrationStatus === 'approved' && !this.sharedUtilService.isTmhCorporateAdminUser(responseObject));
	}

	private redirectOnError(error: HttpErrorResponse)
	{
		const errorMessage =
			error?.error?.statusCode === 401
				? error.error.message
				: this.appConstantsRef.messages.filter(
					(x) => x.category === 'Error' && x.tags.includes('default')
				)[0]?.value;
		this.sharedUtilService.triggerErrorService(errorMessage, error?.error?.statusCode, error?.error?.id);
		this.sharedService.navigateMethod(this.router, ApplicationPath.unAuthError);
	}

	private async getUserRegistrationStatus(): Promise<ApiResponse>
	{
		try
		{
			return await this.apiService.getUserRegistrationStatus();
		}
		catch (error)
		{
			this.loggerService.log('Error redirecting to logout', error);
			throw error;
		}
	}

	getLoggedInUserDetails(routingFunction : RoutingFunction | undefined)
	{
		forkJoin([ this.apiService.getLoggedInUserObs().pipe(this.handleRequest().bind(this)), this.apiService.getLoggedInUserLocations().pipe(this.handleRequest().bind(this)) ]).subscribe({ next: (res)=>
		{
			if(res[0] && res[1])
			{
				const user = <AuthState>(res[0]?.data);
				this.user = user;
				this.localStorageService.setLoggedInUser(this.localStorageService.striginifyJSON(user));

				const locations:ProfileLocation = <ProfileLocation>(res[1]?.data);
				this.localStorageService.setLoggedInUserLocation(this.localStorageService.striginifyJSON(locations));

				if(this.localStorageService.getLoggedInUser() && this.localStorageService.getLoggedInUserLocation())
				{
					this.sharedUtilService.handleRoutingFunction(routingFunction, this.router);
				}
			}
			else
			{
				this.handleCommonError(ErrorMessage.commonError, 100, '401');
			}
		}
		});
	}

	getLoggedInTmhCorporateAdminDetails(routingFunction : RoutingFunction | undefined)
	{
		this.apiService.getLoggedInTmhCorporateAdminDetails().pipe(this.handleRequest().bind(this)).subscribe({ next: (res)=>
		{
			if(res)
			{
				const user = <TMHCorporateAdminAuthState>(res.data);
				this.sharedApiService.mapTmhCorporateAdminUserSession(user);

				if(this.localStorageService.getTmhCorporateAdminDetails())
				{
					this.sharedUtilService.handleRoutingFunction(routingFunction, this.router);
				}
			}
			else
			{
				this.handleCommonError(ErrorMessage.commonError, 100, '401');
			}
		}
		});
	}
	getLoggedInTmhCustomerSupportDetails(routingFunction : RoutingFunction | undefined)
	{
		this.apiService.getLoggedInTmhCustomerSupportDetails().pipe(this.handleRequest().bind(this)).subscribe({ next: (res)=>
		{
			if(res)
			{
				const user = <TMHCorporateAdminAuthState>(res.data);
				this.sharedApiService.mapTmhCustomerSupportUserSession(user);

				if(this.localStorageService.getTmhCustomerSupportDetails())
				{
					this.sharedUtilService.handleRoutingFunction(routingFunction, this.router);
				}
			}
			else
			{
				this.handleCommonError(ErrorMessage.commonError, 100, '401');
			}
		}
		});
	}

	handleRequest() 
	{
		return function (observable: Observable<any>) 
		{
			return observable.pipe(
			  map((result) => 
				{
					return result;
			  }),
			  catchError(() => 
				{
					return of(null);
				})
			);
		  };
	}
	handleCommonError(status: string, statusCode: number, id:string)
	{
		this.sharedUtilService.triggerErrorService(status, statusCode, id);
		this.sharedService.navigateMethod(this.router, ApplicationPath.authError);
	}

	handleCustomerNavigation(responseObject: UserRegistrationStatus, isInSession: boolean)
	{
		if(this.sharedUtilService.isCustomerSupportUser(responseObject, false) && isInSession)
		{
		    const impersonateUser = this.localStorageService.getImpersonateUser();
			if(impersonateUser === null || impersonateUser === undefined)
			{
				this.localStorageService.setCustomerToDashboard('false');
				this.localStorageService.removeImpersonateUser();
				this.localStorageService.setUserName('');
				this.sharedService.navigateMethod(this.router, 'app/customer-support');
				this.sharedUtilService.isCustomerSupportSession$.next(false);
				return;
			}
		}
	}
	loadZendeskChat(): void 
	{
		try
		{
			this.apiService.getZendeskToken().subscribe(response => 
			{
			  const zentoken = <ZendeskToken>response.data;
				(window as any)['zE']('messenger', 'loginUser',
					function(callback: any) 
					{
						callback(zentoken.token);
					}
				);
			});
		}
		catch (error)
		{
			this.loggerService.log('error while logging in user', error);
		}
	}
}
